import { useTeamMembers } from '@/hooks/useTeamMembers'
import useUserPermissions from '@/hooks/useUserPermissions'
import { TEAMS_EXTRA_SEAT_PRICE } from '@/utils/data/plans'
import { Minus, Plus } from '@mynaui/icons-react'
import { DialogPortal } from '@radix-ui/react-dialog'
import { useState } from 'react'
import { PurchaseTeamSeat } from './TeamsSeatPurchase'
import { Stack, Text } from './custom-components'
import { Button } from './shadcn/button'
import { Dialog, DialogContent } from './shadcn/dialog'
import { Skeleton } from './shadcn/skeleton'

const UserProfileDialogTeamsSeatPurchaseSection = () => {
  const [open, onOpenChange] = useState<boolean>(false)
  const [seatPurchaseCount, setSeatPurchaseCount] = useState<number>(0)
  const { userTeams } = useUserPermissions()
  const { teamMembers, isLoadingTeamMembers, user, currentUserIsOwner } = useTeamMembers()
  const seatsLimitReached =
    teamMembers &&
    userTeams &&
    userTeams.length > 0 &&
    teamMembers.length >= (userTeams.find((team) => team.id == user?.teamId)?.seats ?? 0)
  const incrementValue = () => {
    setSeatPurchaseCount(seatPurchaseCount + 1)
  }
  const decrementValue = () => {
    setSeatPurchaseCount(Math.max(seatPurchaseCount - 1, 0))
  }
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogPortal>
          <DialogContent className={'overflow-y-auto max-h-screen bg-themebackground dark:bg-themebackgrounddark'}>
            <PurchaseTeamSeat newSeatCount={seatPurchaseCount} />
          </DialogContent>
        </DialogPortal>
      </Dialog>
      <Stack
        className={`p-4 gap-4 rounded-2xl border border-themesidebarborder dark:border-themesidebarborderdark overflow-hidden ${currentUserIsOwner ? '' : 'opacity-50'}`}
      >
        <div className="flex justify-between items-center gap-4">
          <Stack className="gap-1">
            <Text size={'base'} weight={'semibold'} className="leading-6">
              Add or Remove Seats
            </Text>
            <Text size={'sm'} weight={'normal'} className="text-thememutedforeground leading-5">
              Each additional seat is $${TEAMS_EXTRA_SEAT_PRICE}/mo
            </Text>
          </Stack>

          <div className="h-10 max-w-32 w-full p-2 flex items-center justify-between space-x-4 border border-themesidebarborder dark:border-themesidebarborderdark bg-themebackground dark:bg-themebackgrounddark rounded-lg">
            <Button variant="ghost" size="iconXSm" onClick={decrementValue} disabled={!currentUserIsOwner}>
              <Minus className="h-4 w-4" />
              <span className="sr-only">Decrement</span>
            </Button>
            <Text weight="semibold" size="sm" className="w-fit m-0">
              {seatPurchaseCount}
            </Text>
            <Button variant="ghost" size="iconXSm" onClick={incrementValue} disabled={!currentUserIsOwner}>
              <Plus className="h-4 w-4" />
              <span className="sr-only">Increment</span>
            </Button>
          </div>
        </div>
        <div
          className={`flex flex-col gap-1 w-full border ${seatsLimitReached ? 'border-yellow-500 bg-yellow-50' : 'border-themesidebarborder dark:border-themesidebarborderdark'} rounded-md px-4 py-3`}
        >
          {isLoadingTeamMembers ? (
            <Skeleton className="h-4 w-full max-w-[250px]" />
          ) : teamMembers ? (
            <Text
              size={'sm'}
              weight={seatsLimitReached ? 'semibold' : 'medium'}
              className={seatsLimitReached ? 'text-[#9A3413]' : 'text-thememutedforeground'}
            >
              {teamMembers.length} out of {userTeams?.find((team) => team.id == user?.teamId)?.seats} seats used{' '}
            </Text>
          ) : null}
          {seatsLimitReached ? (
            <Text size={'sm'} weight={'normal'} className="text-[#ED6A31]">
              All seats in your current plan are filled. Extra users cost $${TEAMS_EXTRA_SEAT_PRICE}/mo each.{' '}
            </Text>
          ) : null}
        </div>
        <div className="flex justify-end items-center gap-8">
          <Button
            variant={'destructivePurple'}
            disabled={seatPurchaseCount < 1 || !currentUserIsOwner}
            onClick={() => onOpenChange(true)}
          >
            Purchase Seat
          </Button>
        </div>
      </Stack>
    </>
  )
}

export default UserProfileDialogTeamsSeatPurchaseSection
